import { Toast } from '@base-ui/react/toast';
import type { NotificationOptions } from '@/shared/ui/notification/types';

/**
 * The application's single toast manager.
 *
 * Created at MODULE scope, not inside the provider, for two reasons:
 *
 * 1. Base UI's `ToastProvider` subscribes to a manager passed to it and keeps
 *    its own store. The manager is the thing that survives a re-render, so it
 *    must not be recreated when the provider re-renders.
 * 2. A module-scope manager can be reached from outside React — a route loader,
 *    an error boundary, a non-component module — through `notifyFromOutside`.
 *    A manager created inside a component cannot.
 *
 * This is the ONLY manager in the application. A second one would render into a
 * viewport that does not exist and the notification would vanish silently.
 */
export const toastManager = Toast.createToastManager();

/**
 * The default lifetime of a notification, in milliseconds.
 *
 * `0` means the notification stays until the operator dismisses it. That is the
 * default because a notification here reports the outcome of an action the
 * operator took, and an outcome that disappears on its own is one they can miss
 * — a failure they never saw is a failure they will repeat. A caller that wants
 * a self-clearing acknowledgement passes an explicit `timeout`.
 */
const DEFAULT_TIMEOUT = 0;

/**
 * Raise a notification from OUTSIDE a React component.
 *
 * A route loader, an error boundary or a plain module cannot call the
 * `useNotification` hook. This reaches the same manager the provider renders
 * from, so the notification appears in the same viewport with the same styling.
 *
 * Prefer the hook inside components — it is the documented path and it keeps the
 * dependency visible. This exists for the cases where there is no component to
 * call it from, not as a general shortcut.
 */
export function notifyFromOutside(options: NotificationOptions): string {
  return toastManager.add({
    title: options.title,
    description: options.description,
    type: options.tone ?? 'info',
    timeout: options.timeout ?? DEFAULT_TIMEOUT,
    priority: options.tone === 'error' ? 'high' : 'low',
    actionProps:
      options.action === undefined
        ? undefined
        : { children: options.action.label, onClick: options.action.onClick },
  });
}
